import { format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React from "react";
import { createRoot } from "react-dom/client";
import toast from "react-hot-toast";
export const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return format(new Date(dateString), "yyyy-MM-dd");
  } catch (error) {
    return dateString;
  }
};

export const InvoiceTemplate = React.forwardRef(({ bill, schoolInfo }, ref) => {
  // Use campus data from bill if available, fallback to schoolInfo
  const campus = bill?.campus || {};
  const schoolName = campus?.name || schoolInfo.name;
  const schoolLocation = campus?.location || schoolInfo.address;
  const schoolEmail = campus?.contactEmail || schoolInfo.email;
  const schoolPhone = campus?.contactNumber || schoolInfo.phone;
  
  return (
    <div ref={ref} className="invoice-template bg-white p-8" style={{ width: "210mm", minHeight: "297mm" }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#241763]">INVOICE</h1>
          <p className="text-gray-600">Invoice #: INV-{bill.enrollmentNo?.substring(4, 12) || "00000"}</p>
          <p className="text-gray-600">Date: {format(new Date(), "yyyy-MM-dd")}</p>
        </div>
        <div className="text-right">
          <h2 className="text-xl font-bold">{schoolName}</h2>
          <p>{schoolLocation}</p>
          {schoolEmail && <p>Email: {schoolEmail}</p>}
          <p>Tel: {schoolPhone}</p>
        </div>
      </div>

      {/* Student Info */}
      <div className="mb-8 flex justify-between">
        <div>
          <h3 className="font-bold mb-2">Bill To: {bill.fullName || "Student Name"}</h3>
          <p>Enrollment No: {bill.enrollmentNo || "N/A"}</p>
          {campus?.name && <p>Campus: {campus.name}</p>}
        </div>
        <div>
          <h3 className="font-bold mb-2">Payment Details:</h3>
          <p>Status: <span className={`font-bold ${(bill.status === "Overdue") ? "text-red-600" : "text-orange-500"}`}>{bill.status || "Pending"}</span></p>
          <p>Due Date: {formatDate(bill.dueDate)}</p>
          <p>Bill Period: {formatDate(bill.billingCycleStartDate)} to {formatDate(bill.billingCycleEndDate)}</p>
        </div>
      </div>

      {/* Bill Details */}
      <div className="mb-8">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left">Description</th>
              <th className="border p-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border p-2">Tuition Fee</td>
              <td className="border p-2 text-right">${(bill.totalFee || 0).toFixed(2)}</td>
            </tr>
            {(bill.discountAmount > 0) && (
              <tr>
                <td className="border p-2">Discount</td>
                <td className="border p-2 text-right text-red-600">-${(bill.discountAmount || 0).toFixed(2)}</td>
              </tr>
            )}
            <tr className="bg-gray-50">
              <td className="border p-2 font-bold">Total</td>
              <td className="border p-2 text-right font-bold">${(bill.finalAmount || 0).toFixed(2)}</td>
            </tr>
            {(bill.pendingAmount < bill.finalAmount) && (
              <tr>
                <td className="border p-2">Amount Paid</td>
                <td className="border p-2 text-right">${((bill.finalAmount || 0) - (bill.pendingAmount || 0)).toFixed(2)}</td>
              </tr>
            )}
            <tr className="bg-gray-100">
              <td className="border p-2 font-bold">Amount Due</td>
              <td className="border p-2 text-right font-bold text-red-600">${(bill.pendingAmount || 0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Instructions */}
      <div className="mb-8">
        <h3 className="font-bold mb-2">Payment Instructions:</h3>
        <p>Please make payment before the due date to avoid late fees.</p>
        {campus?.contactEmail && <p>For inquiries, contact: {campus.contactEmail}</p>}
      </div>

      {/* Terms and Conditions */}
      <div className="text-sm text-gray-600 mt-12 pt-4 border-t">
        <p className="mb-1"><strong>Terms & Conditions:</strong></p>
        <p>1. Payment is due by the specified due date.</p>
        <p>2. Late payments may incur additional fees.</p>
        <p>3. For any queries regarding this invoice, please contact the finance department.</p>
        <p className="mt-4 text-center">Thank you for your prompt payment!</p>
      </div>
    </div>
  );
});

export const generateInvoicePDF = async (bill, schoolInfo) => {
  try {
    toast.loading("Generating invoice...");
    
    // Create a container div for the invoice
    const invoiceContainer = document.createElement('div');
    invoiceContainer.style.position = 'absolute';
    invoiceContainer.style.left = '-9999px';
    invoiceContainer.style.top = '-9999px';
    document.body.appendChild(invoiceContainer);
    const root = createRoot(invoiceContainer);
  
    return new Promise((resolve) => {
      root.render(<InvoiceTemplate bill={bill} schoolInfo={schoolInfo} />);
      setTimeout(async () => {
        try {
          const invoiceElement = invoiceContainer.querySelector('.invoice-template');
          
          if (!invoiceElement) {
            throw new Error("Invoice element not found");
          }
          
          // Create PDF with proper settings
          const canvas = await html2canvas(invoiceElement, {
            scale: 1.5, // Higher scale for better quality
            logging: false,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff"
          });
          
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          });
          
          const imgData = canvas.toDataURL('image/jpeg', 1.0); // Use JPEG for better compatibility
          const imgWidth = 210; // A4 width in mm
          const pageHeight = 297; // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
          
          // Handle multi-page if needed
          let heightLeft = imgHeight - pageHeight;
          let position = -pageHeight;
          
          while (heightLeft > 0) {
            position = position - pageHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
          
          // Use campus name in filename if available
          const campusName = bill.campus?.name ? `-${bill.campus.name}` : '';
          pdf.save(`Invoice-${bill.enrollmentNo || 'unknown'}${campusName}.pdf`);
          
          toast.dismiss();
          toast.success("Invoice generated successfully!");
          resolve();
        } catch (error) {
          toast.dismiss();
          toast.error("Failed to generate invoice: " + error.message);
          console.error("Error in PDF generation:", error);
          resolve();
        } finally {
          // Clean up
          setTimeout(() => {
            root.unmount();
            document.body.removeChild(invoiceContainer);
          }, 100);
        }
      }, 1000); // Give more time for rendering
    });
  } catch (error) {
    toast.dismiss();
    toast.error("Error preparing invoice: " + error.message);
    console.error("Error preparing invoice:", error);
  }
};

// Updated default school info to include email field
export const defaultSchoolInfo = {
  name: "WiserBee Academy",
  address: "123 Education Street",
  city: "Learning City",
  postalCode: "A1B 2C3",
  phone: "(123) 456-7890",
  email: "info@wiserbeeeducation.com",
};

export const generatePDFFromElement = async (elementRef, filename = "Report.pdf", scale = 2) => {
  if (!elementRef.current) return;
  
  const input = elementRef.current;
  const buttons = input.querySelectorAll('.no-print');
  
  // Hide buttons before capturing
  buttons.forEach(button => button.style.display = 'none');

  try {
    const canvas = await html2canvas(input, { scale });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    // Restore buttons visibility
    buttons.forEach(button => button.style.display = '');
  }
};




export const generateStudentReportPDF = (studentReport, campusName, filename = "Student_Exam_Report.pdf") => {
  if (!studentReport) {
    console.error("No student report data available");
    return;
  }
  
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const academicYear = "2023-24"; // You can make this dynamic
  
  // Add header with school information (logo removed)
  let y = 15;
  
  // School Name
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text(campusName, pageWidth/2, y, { align: "center" });
  
  y += 8;
  
  // Report Title
  pdf.setFontSize(14);
  pdf.text("End of Year Report", pageWidth/2, y, { align: "center" });
  
  y += 6;
  
  // Academic Year
  pdf.setFontSize(12);
  pdf.text(academicYear, pageWidth/2, y, { align: "center" });
  
  y += 6;
  
  // Level (A-Levels or O-Levels)
  const level = studentReport.className?.includes('-') ? 'A-Levels' : 'O-Levels';
  pdf.setFont("helvetica", "medium");
  pdf.text(level, pageWidth/2, y, { align: "center" });
  
  y += 12;
  
  // Student Info
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  
  // Student Name
  pdf.setFont("helvetica", "bold");
  pdf.text("Student Name", margin, y);
  pdf.setFont("helvetica", "normal");
  
  // Draw line for student name
  const nameLineStart = margin + 30;
  pdf.line(nameLineStart, y, pageWidth - margin, y);
  pdf.text(studentReport.studentName, nameLineStart + 2, y - 1);
  
  y += 8;
  
  // Class / Section and Date in the same row
  pdf.setFont("helvetica", "bold");
  pdf.text("Class / Section", margin, y);
  pdf.setFont("helvetica", "normal");
  
  // Draw line for class/section
  const classLineStart = margin + 30;
  pdf.line(classLineStart, y, classLineStart + 50, y);
  pdf.text(`${studentReport.className} / ${studentReport.sectionName}`, classLineStart + 2, y - 1);
  
  // Date on the same row
  const dateColStart = classLineStart + 65;
  pdf.setFont("helvetica", "bold");
  pdf.text("Date", dateColStart, y);
  pdf.setFont("helvetica", "normal");
  
  // Draw line for date
  const dateLineStart = dateColStart + 15;
  pdf.line(dateLineStart, y, pageWidth - margin, y);
  pdf.text(new Date().toLocaleDateString('en-GB'), dateLineStart + 2, y - 1);
  
  y += 15;
  
  // Subject Table
  addGradeTable(pdf, studentReport, margin, contentWidth, y);
  
  // Get the updated y position after adding the grade table
  y = pdf.lastY + 10;
  
  // Add overall result section
  y = addOverallResults(pdf, studentReport, margin, contentWidth, y);
  
  // Add activity table
  y = addActivityTable(pdf, margin, contentWidth, y);
  
  // Add performance key
  y = addPerformanceKey(pdf, margin, contentWidth, y);
  
  // Add grade key
  y = addGradeKey(pdf, margin, contentWidth, y);
  
  // Add signature lines - making sure they stay on the same page
  // Check if there's not enough space for signatures and force a page break if needed
  const requiredSpaceForSignatures = 30; // approximate space needed for signatures
  if (y > pageHeight - requiredSpaceForSignatures) {
    pdf.addPage();
    y = 20;
  }
  
  // Add signature lines
  addSignatureLines(pdf, margin, contentWidth, y);
  
  pdf.save(filename);
};

export const generateSectionReportPDF = (
  sectionReports,
  className,
  sectionName,
  campusName,
  filename = "Section_Exam_Report.pdf"
) => {
  if (!sectionReports || sectionReports.length === 0) {
    console.error("No section reports data available");
    return;
  }

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const academicYear = "2023-24"; // You can make this dynamic

  sectionReports.forEach((studentReport, index) => {
    // Add a new page for each student except the first one
    if (index > 0) {
      pdf.addPage();
    }

    let y = 15;

    // School Name
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(campusName, pageWidth/2, y, { align: "center" });

    y += 8;

    // Report Title
    pdf.setFontSize(14);
    pdf.text("End of Year Report", pageWidth/2, y, { align: "center" });

    y += 6;

    // Academic Year
    pdf.setFontSize(12);
    pdf.text(academicYear, pageWidth/2, y, { align: "center" });

    y += 6;

    // Level (A-Levels or O-Levels)
    const level = studentReport.className?.includes('-') ? 'A-Levels' : 'O-Levels';
    pdf.setFont("helvetica", "medium");
    pdf.text(level, pageWidth/2, y, { align: "center" });

    y += 12;

    // Student Info
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");

    // Student Name
    pdf.setFont("helvetica", "bold");
    pdf.text("Student Name", margin, y);
    pdf.setFont("helvetica", "normal");

    // Draw line for student name
    const nameLineStart = margin + 30;
    pdf.line(nameLineStart, y, pageWidth - margin, y);
    pdf.text(studentReport.studentName, nameLineStart + 2, y - 1);

    y += 8;

    // Class / Section and Date in the same row
    pdf.setFont("helvetica", "bold");
    pdf.text("Class / Section", margin, y);
    pdf.setFont("helvetica", "normal");

    // Draw line for class/section
    const classLineStart = margin + 30;
    pdf.line(classLineStart, y, classLineStart + 50, y);
    pdf.text(`${studentReport.className} / ${studentReport.sectionName}`, classLineStart + 2, y - 1);

    // Date on the same row
    const dateColStart = classLineStart + 65;
    pdf.setFont("helvetica", "bold");
    pdf.text("Date", dateColStart, y);
    pdf.setFont("helvetica", "normal");

    // Draw line for date
    const dateLineStart = dateColStart + 15;
    pdf.line(dateLineStart, y, pageWidth - margin, y);
    pdf.text(new Date().toLocaleDateString('en-GB'), dateLineStart + 2, y - 1);

    y += 15;

    // Subject Table
    addGradeTable(pdf, studentReport, margin, contentWidth, y);

    // Get the updated y position after adding the grade table
    y = pdf.lastY + 10;

    // Add overall result section
    y = addOverallResults(pdf, studentReport, margin, contentWidth, y);

    // Add activity table
    y = addActivityTable(pdf, margin, contentWidth, y);

    // Add performance key
    y = addPerformanceKey(pdf, margin, contentWidth, y);

    // Add grade key
    y = addGradeKey(pdf, margin, contentWidth, y);

    // Add signature lines - making sure they stay on the same page
    const requiredSpaceForSignatures = 30; // approximate space needed for signatures
    if (y > pageHeight - requiredSpaceForSignatures) {
      pdf.addPage();
      y = 20;
    }

    // Add signature lines
    addSignatureLines(pdf, margin, contentWidth, y);
  });

  pdf.save(filename);
};

const addGradeTable = (pdf, studentReport, margin, contentWidth, y) => {
  const rowHeight = 8;
  
  // Subject table headers
  const headers = [
    "Subject", 
    "Total Marks", 
    "Obtained Marks", 
    "Percentage", 
    "Grade"
  ];
  
  // Calculate column widths proportionally
  const colWidths = [
    contentWidth * 0.40, // Subject
    contentWidth * 0.15, // Total Marks
    contentWidth * 0.15, // Obtained Marks
    contentWidth * 0.15, // Percentage
    contentWidth * 0.15  // Grade
  ];
  
  // Draw table header with gray background
  pdf.setFillColor(242, 242, 242);
  pdf.rect(margin, y, contentWidth, rowHeight, 'F');
  
  // Add header text
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  
  let xPos = margin;
  headers.forEach((header, i) => {
    const align = i === 0 ? 'left' : 'center';
    const textX = i === 0 ? xPos + 2 : xPos + colWidths[i]/2;
    pdf.text(header, textX, y + 5.5, i === 0 ? {} : { align: 'center' });
    xPos += colWidths[i];
  });
  
  // Draw header borders
  drawTableRow(pdf, margin, y, colWidths, rowHeight);
  
  y += rowHeight;
  
  // Draw subjects rows
  pdf.setFont("helvetica", "normal");
  
  if (studentReport.subjects && studentReport.subjects.length > 0) {
    studentReport.subjects.forEach((subject, index) => {
      // Check if we need a page break
      if (y > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        y = 15;
        
        // Redraw the table header on the new page
        pdf.setFillColor(242, 242, 242);
        pdf.rect(margin, y, contentWidth, rowHeight, 'F');
        
        pdf.setFont("helvetica", "bold");
        let xPos = margin;
        headers.forEach((header, i) => {
          const textX = i === 0 ? xPos + 2 : xPos + colWidths[i]/2;
          pdf.text(header, textX, y + 5.5, i === 0 ? {} : { align: 'center' });
          xPos += colWidths[i];
        });
        
        drawTableRow(pdf, margin, y, colWidths, rowHeight);
        y += rowHeight;
        pdf.setFont("helvetica", "normal");
      }
      
      // Draw subject row
      xPos = margin;
      
      // Subject name (left aligned)
      pdf.text(subject.subjectName, xPos + 2, y + 5.5);
      xPos += colWidths[0];
      
      // Total marks (center aligned)
      pdf.text(subject.totalMarks.toString(), xPos + colWidths[1]/2, y + 5.5, { align: 'center' });
      xPos += colWidths[1];
      
      // Obtained marks (center aligned)
      pdf.text(subject.obtainedMarks.toFixed(2).toString(), xPos + colWidths[2]/2, y + 5.5, { align: 'center' });
      xPos += colWidths[2];
      
      // Percentage (center aligned)
      pdf.text(subject.percentage.toFixed(2) + "%", xPos + colWidths[3]/2, y + 5.5, { align: 'center' });
      xPos += colWidths[3];
      
      // Grade (center aligned and bold)
      pdf.setFont("helvetica", "bold");
      pdf.text(subject.grade, xPos + colWidths[4]/2, y + 5.5, { align: 'center' });
      pdf.setFont("helvetica", "normal");
      
      // Draw row borders
      drawTableRow(pdf, margin, y, colWidths, rowHeight);
      
      y += rowHeight;
    });
  } else {
    // No subjects case
    pdf.text("No subjects found", margin + contentWidth/2, y + 5.5, { align: 'center' });
    drawTableRow(pdf, margin, y, [contentWidth], rowHeight);
    y += rowHeight;
  }
  
  // Store the last y position for the caller function
  pdf.lastY = y;
  
  return y;
};

const addOverallResults = (pdf, studentReport, margin, contentWidth, y) => {
  const boxWidth = contentWidth / 2;
  const boxHeight = 16;
  
  // Create two boxes side by side
  // Overall Percentage Box
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.5);
  pdf.rect(margin, y, boxWidth, boxHeight);
  
  // Title
  pdf.setFillColor(242, 242, 242);
  pdf.rect(margin, y, boxWidth, boxHeight/2, 'F');
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Overall %age", margin + boxWidth/2, y + boxHeight/4 + 1, { align: 'center' });
  
  // Value
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(studentReport.overallPercentage.toString(), margin + boxWidth/2, y + boxHeight * 0.75, { align: 'center' });
  
  // Overall Grade Box
  pdf.setDrawColor(0);
  pdf.rect(margin + boxWidth, y, boxWidth, boxHeight);
  
  // Title
  pdf.setFillColor(242, 242, 242);
  pdf.rect(margin + boxWidth, y, boxWidth, boxHeight/2, 'F');
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Overall Grade", margin + boxWidth * 1.5, y + boxHeight/4 + 1, { align: 'center' });
  
  // Value
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(studentReport.overallGrade, margin + boxWidth * 1.5, y + boxHeight * 0.75, { align: 'center' });
  
  return y + boxHeight + 10;
};

const addActivityTable = (pdf, margin, contentWidth, y) => {
  const rowHeight = 8;
  
  // Headers
  const headers = ["Activity", "Punctuality", "Conduct", "Co-Curricular"];
  const colWidths = Array(4).fill(contentWidth / 4);
  
  // Draw header with gray background
  pdf.setFillColor(220, 220, 220);
  pdf.rect(margin, y, contentWidth, rowHeight, 'F');
  
  // Add header text
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  
  let xPos = margin;
  headers.forEach((header, i) => {
    pdf.text(header, xPos + colWidths[i]/2, y + 5.5, { align: 'center' });
    xPos += colWidths[i];
  });
  
  // Draw header borders
  drawTableRow(pdf, margin, y, colWidths, rowHeight);
  
  y += rowHeight;
  
  // GPI row
  pdf.setFillColor(220, 220, 220);
  pdf.rect(margin, y, colWidths[0], rowHeight, 'F');
  pdf.setFont("helvetica", "bold");
  pdf.text("GPI", margin + colWidths[0]/2, y + 5.5, { align: 'center' });
  
  // Activity values (ME for all columns)
  pdf.setFont("helvetica", "normal");
  for (let i = 1; i < 4; i++) {
    const xPos = margin + colWidths[0] + ((i-1) * colWidths[i]);
    pdf.text("ME", xPos + colWidths[i]/2, y + 5.5, { align: 'center' });
  }
  
  // Draw row borders
  drawTableRow(pdf, margin, y, colWidths, rowHeight);
  
  return y + rowHeight + 10;
};

const addPerformanceKey = (pdf, margin, contentWidth, y) => {
  const rowHeight = 16;
  
  // Header
  pdf.setFillColor(220, 220, 220);
  pdf.rect(margin, y, contentWidth, rowHeight/2, 'F');
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("General Performance Key", margin + contentWidth/2, y + rowHeight/4 + 1, { align: 'center' });
  
  // Draw header border
  pdf.setDrawColor(0);
  pdf.rect(margin, y, contentWidth, rowHeight/2);
  
  y += rowHeight/2;
  
  // Performance key items
  const keyItems = [
    { code: "SE", desc: "Significantly Exceeds Expectation" },
    { code: "EE", desc: "Exceeds Expectation" },
    { code: "ME", desc: "Meets Expectation" },
    { code: "FE", desc: "Fails to Meet Expectations" }
  ];
  
  const colWidth = contentWidth / 4;
  
  // Draw cells and content
  keyItems.forEach((item, i) => {
    const xPos = margin + (i * colWidth);
    
    pdf.setFont("helvetica", "bold");
    pdf.text(item.code, xPos + 4, y + 5);
    
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(item.desc, xPos + 4, y + 12);
    
    // Draw cell borders
    pdf.setDrawColor(0);
    pdf.rect(xPos, y, colWidth, rowHeight/2);
  });
  
  return y + rowHeight/2 + 10;
};

const addGradeKey = (pdf, margin, contentWidth, y) => {
  const rowHeight = 10;
  
  // Grades
  const grades = [
    { range: "90+", grade: "A+" },
    { range: "80+", grade: "A" },
    { range: "70+", grade: "B" },
    { range: "60+", grade: "C" },
    { range: "50+", grade: "D" },
    { range: "40+", grade: "E" },
    { range: "Below 40", grade: "U" }
  ];
  
  // First column is for "Grade Key" text
  const colWidths = [contentWidth * 0.2];
  // Remaining columns for grade ranges
  const gradeColWidth = (contentWidth * 0.8) / grades.length;
  for (let i = 0; i < grades.length; i++) {
    colWidths.push(gradeColWidth);
  }
  
  // Draw header with gray background
  pdf.setFillColor(220, 220, 220);
  pdf.rect(margin, y, contentWidth, rowHeight, 'F');
  
  // Add "Grade Key" text
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Grade Key", margin + colWidths[0]/2, y + 6, { align: 'center' });
  
  // Add grade ranges and letters
  let xPos = margin + colWidths[0];
  grades.forEach((gradeInfo, i) => {
    pdf.text(`${gradeInfo.range}\n${gradeInfo.grade}`, xPos + gradeColWidth/2, y + 4, { 
      align: 'center',
      lineHeightFactor: 1.5
    });
    xPos += gradeColWidth;
  });
  
  // Draw table borders
  drawTableRow(pdf, margin, y, colWidths, rowHeight);
  
  return y + rowHeight + 10; // Reduced spacing to ensure signatures fit on same page
};

const addSignatureLines = (pdf, margin, contentWidth, y) => {
  const signatureWidth = contentWidth / 3;
  const signatureLineY = y + 15;
  
  // Draw signature lines and labels
  const signatures = [
    "Coordinator's Signature",
    "Headmistress's Signature",
    "Principal / Vice Principal's Signature"
  ];
  
  signatures.forEach((label, i) => {
    const xPos = margin + (i * signatureWidth) + signatureWidth/2;
    
    // Draw signature line
    pdf.setDrawColor(0);
    pdf.line(margin + (i * signatureWidth) + 10, signatureLineY, 
             margin + ((i+1) * signatureWidth) - 10, signatureLineY);
    
    // Add label
    pdf.setFontSize(8);
    pdf.text(label, xPos, signatureLineY + 5, { align: 'center' });
  });
  
  return signatureLineY + 10;
};

// Helper function to draw table rows with borders
const drawTableRow = (pdf, x, y, colWidths, height) => {
  pdf.setDrawColor(0);
  
  // Draw horizontal lines (top and bottom of row)
  pdf.line(x, y, x + colWidths.reduce((a, b) => a + b, 0), y);
  pdf.line(x, y + height, x + colWidths.reduce((a, b) => a + b, 0), y + height);
  
  // Draw vertical lines
  let xPos = x;
  pdf.line(xPos, y, xPos, y + height); // First vertical line
  
  colWidths.forEach(width => {
    xPos += width;
    pdf.line(xPos, y, xPos, y + height); // Column divider line
  });
};